import {t} from "@lingui/core/macro";

import type {PhosphorIconAsset} from "@khanacademy/wonder-blocks-icon";

import type {JsonPath, JsonValue} from "./types.ts";

import plusIcon from "@phosphor-icons/core/bold/plus-bold.svg";
import arrowsClockwiseIcon from "@phosphor-icons/core/regular/arrows-clockwise.svg";
import chatsIcon from "@phosphor-icons/core/regular/chats.svg";
import pencilCircleIcon from "@phosphor-icons/core/regular/pencil-circle.svg";
import pencilIcon from "@phosphor-icons/core/regular/pencil.svg";
import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";
import questionIcon from "@phosphor-icons/core/regular/question.svg";
import swapIcon from "@phosphor-icons/core/regular/swap.svg";

export type SelectedAction =
    | "CHAT_ABOUT_THIS"
    | "USE_A_SIMPLER_WORD_HERE"
    | "WHAT_DOES_THIS_MEAN"
    | "MAKE_CHANGES_TO_THIS"
    | "TRY_SOMETHING_DIFFERENT"
    | "NAME_THIS_SECTION"
    | "ADD_ANOTHER_ITEM"
    | "FILL_THIS_IN_FOR_ME";

export interface Suggestion {
    kind: "chat" | "change";
    text: () => string;
    icon: PhosphorIconAsset;
    needsAdditionalInput?: boolean;
    additionalText?: string;
    // Used for CEDAR event
    action: SelectedAction;
}

/**
 * A list of all the suggestions we support.
 * Each suggestion has a text label
 * and an icon.
 **/
export const suggestionList: Record<string, Suggestion> = {
    addContent: {
        kind: "change",
        text: () => t`Add content`,
        icon: plusCircleIcon,
        needsAdditionalInput: true,
        action: "FILL_THIS_IN_FOR_ME",
    },
    chatAboutThis: {
        kind: "chat",
        text: () => t`Discuss this`,
        icon: chatsIcon,
        action: "CHAT_ABOUT_THIS",
    },
    useSimplerWord: {
        kind: "change",
        text: () => t`Use a simpler word here`,
        icon: swapIcon,
        action: "USE_A_SIMPLER_WORD_HERE",
    },
    whatDoesThisMean: {
        kind: "chat",
        text: () => t`What does this mean?`,
        icon: questionIcon,
        action: "WHAT_DOES_THIS_MEAN",
    },
    makeChanges: {
        kind: "change",
        text: () => t`Make changes to this`,
        icon: pencilIcon,
        needsAdditionalInput: true,
        action: "MAKE_CHANGES_TO_THIS",
    },
    trySomethingDifferent: {
        kind: "change",
        text: () => t`Try something different`,
        icon: arrowsClockwiseIcon,
        action: "TRY_SOMETHING_DIFFERENT",
    },
    nameThisSection: {
        kind: "change",
        text: () => t`Name this section`,
        icon: pencilCircleIcon,
        action: "NAME_THIS_SECTION",
    },
    addAnotherItem: {
        kind: "change",
        text: () => t`Add another item`,
        icon: plusIcon,
        action: "ADD_ANOTHER_ITEM",
    },
    fillThisInForMe: {
        kind: "change",
        text: () => t`Fill this in for me`,
        icon: pencilCircleIcon,
        action: "FILL_THIS_IN_FOR_ME",
    },
} as const;

export const defaultSuggestions: Suggestion[] = [
    suggestionList.makeChanges,
    suggestionList.trySomethingDifferent,
    suggestionList.chatAboutThis,
];

export const getSuggestionsForPath = (root: JsonValue, path: JsonPath) => {
    if (!root || !path || !path.length) {
        return [];
    }

    // TOOD(reid): Implement more clever suggestions based on what
    // data is at the path. For now, we just return the default suggestions.
    return defaultSuggestions;
};
